import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  GripVerticalIcon,
  PencilIcon,
  TrashIcon,
  Eye,
  Smartphone,
  Monitor,
} from 'lucide-react';
import {
  Badge,
  Box,
  Icon,
  IconButton,
  Stack,
  Flex,
  AccordionButton,
  AccordionPanel,
  AccordionItem,
  Text,
  Tooltip,
  Button,
  ButtonGroup,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { OpenQuestion } from './OpenQuestion';
import { UnitAssessmentSlides } from '../types';
import { EditorMode } from '@duke-hq/shared-constants/assessments';
import { useSnapshot } from 'valtio';
import { unitAssessmentState } from '../UnitAssessmentState';
import { incompleteSlideLabel } from '../incompleteSlideLabel';
import { QuestionGroup } from './QuestionGroup';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { DukeTiptapEditor } from '@duke-hq/ui/components/DukeTiptapEditor/DukeTiptapEditor';
import { AssessmentFormRenderer } from '@duke-hq/ui/components/AssessmentForm/AssessmentFormRenderer';
import { EditorHTML } from '@duke-hq/ui/components/DukeTiptapEditor/EditorHTML';
import { hasTiptapContent } from '@duke-hq/lib/dukeTiptapEditorHelpers';
import { PointerEventHandler, useState } from 'react';
import FilePrompt from './FilePrompt';
import { FormSlide } from './FormSlide/FormSlide';
import { SlideKind } from '@duke-hq/svc-api-db';
import { getSlideIcon } from '../unitAssessmentSlides';
import { SlideRequirementChips } from './SlideRequirementChips';

type SlideItemPropTypes = {
  slide: UnitAssessmentSlides[0];
  handleSlideDelete: (id: string) => void;
  handleDeleteMultiChoiceQuestion: (id: string) => void;
  onStartDrag: PointerEventHandler<HTMLButtonElement>;
};

export function SlideItem({
  slide,
  handleSlideDelete,
  handleDeleteMultiChoiceQuestion,
  onStartDrag,
}: SlideItemPropTypes) {
  const unitAssessmentSnap = useSnapshot(unitAssessmentState);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  const incompleteEntry = unitAssessmentSnap.incompleteSlides.find(
    (entry) => entry.id === slide.id,
  );
  const incompleteLabel =
    incompleteEntry == null ? null : incompleteSlideLabel(incompleteEntry.reason);

  const handleDelete = () => {
    handleSlideDelete(slide.id);
    setIsAlertOpen(false);
  };

  return (
    <>
      <AccordionItem border={'none'}>
        {({ isExpanded }) => (
          <Stack
            borderWidth="1px"
            borderRadius="8px"
            p="24px"
            flex="1"
            w={'full'}
            mb={4}
            borderColor={
              unitAssessmentSnap.currentSlide?.id === slide.id
                ? 'brand.500'
                : incompleteLabel
                  ? 'red.400'
                  : 'border.default'
            }
            boxShadow={unitAssessmentSnap.currentSlide?.id === slide.id ? 'sm' : 'xs'}
            transform={
              unitAssessmentSnap.currentSlide?.id === slide.id ? 'scale(1.01)' : 'scale(1)'
            }
            transition="0.2s"
            bg="bg.canvas"
          >
            <Flex alignItems="center" justifyContent="space-between">
              <Flex alignItems="center" gap="2">
                {slide.kind != null ? getSlideIcon(slide.kind) : null}
                <Text textStyle="lg" fontWeight="medium">
                  {slide.title}
                </Text>
                {incompleteLabel ? (
                  <Tooltip label={incompleteLabel}>
                    <Badge colorScheme="red" variant="subtle">
                      <Trans>Incomplete</Trans>
                    </Badge>
                  </Tooltip>
                ) : null}
              </Flex>
              <ButtonGroup alignItems="center">
                <Tooltip label={isExpanded ? t`Collapse` : t`Expand`}>
                  <IconButton
                    aria-label={t`Expand and collapse slide content`}
                    as={AccordionButton}
                    variant="ghost"
                    size="sm"
                    icon={
                      <Icon
                        as={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                        color="fg.default"
                        boxSize="18px"
                      />
                    }
                  />
                </Tooltip>
                {slide.kind === SlideKind.FORM && (
                  <Tooltip label={t`Preview form`}>
                    <IconButton
                      aria-label={t`Preview form`}
                      variant="ghost"
                      size="sm"
                      icon={<Icon as={Eye} color="fg.default" boxSize="18px" />}
                      onClick={() => {
                        setIsPreviewOpen(true);
                      }}
                    />
                  </Tooltip>
                )}
                <Tooltip label={t`Edit assessment slide`}>
                  <IconButton
                    aria-label={t`Edit slide content`}
                    variant="ghost"
                    size="sm"
                    icon={<Icon as={PencilIcon} color="fg.default" boxSize="18px" />}
                    onClick={() => {
                      if (slide.kind == null) {
                        return;
                      }
                      unitAssessmentState.setCurrentSlide(slide);
                      unitAssessmentState.setEditorMode(EditorMode.EDIT);
                      if (slide.kind !== null) {
                        unitAssessmentState.setEditorType(slide.kind);
                      }
                      unitAssessmentState.openEditor();
                    }}
                  />
                </Tooltip>

                <Tooltip label={t`Delete assessment slide`}>
                  <IconButton
                    aria-label={t`Delete assessment slide`}
                    variant="ghost"
                    size="sm"
                    icon={<Icon as={TrashIcon} color="fg.default" boxSize="18px" />}
                    onClick={() => {
                      setIsAlertOpen(true);
                    }}
                  />
                </Tooltip>
                <IconButton
                  _hover={{ color: 'brand.600' }}
                  icon={<Icon as={GripVerticalIcon} />}
                  cursor="grab"
                  size="sm"
                  variant="tertiary"
                  aria-label={t`Reorder assessment slide`}
                  onPointerDown={onStartDrag}
                />
              </ButtonGroup>
            </Flex>

            <SlideRequirementChips
              slideId={slide.id}
              unitAssessmentVersionId={slide.unitAssessmentVersionId}
            />

            <AccordionPanel p={0}>
              <Stack spacing="4" width="full">
                <Box py="8px" borderRadius="8px">
                  {slide.kind === SlideKind.OPEN_QUESTION && <OpenQuestion slide={slide} />}
                  {slide.kind === SlideKind.FILE_UPLOAD_PROMPT && <FilePrompt slide={slide} />}
                  {slide.kind === SlideKind.QUESTION_GROUP && (
                    <QuestionGroup
                      slide={slide}
                      handleDeleteMultiChoiceQuestion={handleDeleteMultiChoiceQuestion}
                    />
                  )}
                  {slide.kind === SlideKind.CONTENT && (
                    <Box w="full" h="full">
                      <DukeTiptapEditor
                        readOnly
                        initialContent={slide.content}
                        onChange={() => {}}
                      />
                    </Box>
                  )}
                  {slide.kind === SlideKind.FORM && <FormSlide slide={slide} />}
                </Box>
              </Stack>
            </AccordionPanel>
          </Stack>
        )}
      </AccordionItem>

      <ConfirmationDialog
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        title={<Trans>Delete Assessment Slide</Trans>}
        body={
          <Trans>
            You are about to delete this assessment slide. Are you sure you want to continue?
          </Trans>
        }
        onConfirm={handleDelete}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
      />

      <Modal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent
          minW={previewMode === 'mobile' ? '420px' : '750px'}
          maxW={previewMode === 'mobile' ? '420px' : '900px'}
        >
          <ModalHeader>
            <ButtonGroup size="sm" spacing={0} isAttached variant="outline">
              <Button
                leftIcon={<Icon as={Monitor} boxSize="16px" />}
                variant={previewMode === 'desktop' ? 'solid' : 'outline'}
                onClick={() => setPreviewMode('desktop')}
                borderRightRadius={0}
              >
                <Trans>Desktop</Trans>
              </Button>
              <Button
                leftIcon={<Icon as={Smartphone} boxSize="16px" />}
                variant={previewMode === 'mobile' ? 'solid' : 'outline'}
                onClick={() => setPreviewMode('mobile')}
                borderLeftRadius={0}
              >
                <Trans>Mobile</Trans>
              </Button>
            </ButtonGroup>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="2xl" fontWeight="bold" mb={4}>
              {slide.title}
            </Text>

            {'description' in slide && hasTiptapContent(slide.description) ? (
              <EditorHTML content={slide.description} style={{ padding: 0, margin: 0 }} />
            ) : null}

            {slide.kind === SlideKind.FORM && 'sections' in slide ? (
              <AssessmentFormRenderer slide={slide} viewMode={previewMode} isReadOnly={false} />
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setIsPreviewOpen(false)}>
              <Trans>Close</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
